import React, {useEffect, useMemo, useState} from "react";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Col, Row} from "reactstrap";
import Form from "../../base/form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import {OutlinedDropdownForm} from "../../base/dropdown/outlined_dropdown";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import DateUtils from "../../../../core/services/date-utils";
import {MuiRFF, Yup} from '../../form';


const formKeys = {
    make: 'make',
    model: 'model',
    year: 'year',
    vin: 'vin',
    installedDate: 'installedDate',
    failedDate: "failedDate",
    installedMileage: 'installedMileage',
    failedMileage: 'failedMileage',
    remarks: 'remarks',
}

const PartSupportAdditionalInformationDialog = ({makes, models, open, setOpen, onSuccess, additionalInformation, requestId}) => {
    const [loading, setLoading] = useState(false);
    const [isMakeSelected, setIsMakeSelected] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const isMounted = useIsMounted();

    /**
     * Memo version of schema
     */
    const schema = useMemo(() => Yup.object().shape({
        [formKeys.make]: Yup.object().nullable(),
        ...(isMakeSelected ? {[formKeys.model]: Yup.object().nullable()} : {}),
        [formKeys.year]: Yup.string().test('year', 'Invalid Year ',
            (val) => {
                let _val = val?.trim();
                if (!val || val?.length === 0)
                    return true;
                if (val?.length > 0 && val?.trim()?.length === 0)
                    return false;
                if (!Number.isInteger(Number(_val)))
                    return false;
                if (1800 > Number(_val) || Number(_val) > DateUtils.UTCNow().year())
                    return false;
                return _val?.length === 4;
            }
        ).nullable(),
        [formKeys.vin]: Yup.string().test('vin', 'VIN number should be less than 18 characters ',
            (val) => {
                if (!val || val?.length === 0)
                    return true;
                if (val?.length > 0 && val?.trim()?.length === 0)
                    return false;
                return val?.length <= 17;
            }
        ).nullable(),
        [formKeys.installedDate]: Yup.mixed()
            .nullable()
            .validDate(ValidateMessages.incorrect)
            .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
            .maxDate(Yup.ref(formKeys.failedDate), ValidateMessages.lessThan("Failed Date")),
        [formKeys.failedDate]: Yup.mixed()
            .nullable()
            .validDate(ValidateMessages.incorrect)
            .disableFutureDate(ValidateMessages.maxDate('today'), 'day')
            .minDate(Yup.ref(formKeys.installedDate), ValidateMessages.greaterThan("Installed Date")),
        [formKeys.installedMileage]: Yup.number().nullable().typeError(ValidateMessages.incorrectType('number')),
        [formKeys.failedMileage]: Yup.number().nullable().typeError(ValidateMessages.incorrectType('number')),
        [formKeys.remarks]: Yup.string().nullable(),
    }), [isMakeSelected]);

    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    /**
     * With changes in additional information,
     * Sets the initialValues in the inner state
     */
    useEffect(() => {
        if (!!additionalInformation?.modelId) {
            setIsMakeSelected(true);
        }
        setInitialValues({
            [formKeys.make]: !!additionalInformation?.makeId ? makes?.find(e => e.id === additionalInformation.makeId) : undefined,
            [formKeys.model]: !!additionalInformation?.modelId ? models?.find(e => e.id === additionalInformation?.modelId) : undefined,
            [formKeys.year]: additionalInformation?.year ?? undefined,
            [formKeys.vin]: additionalInformation?.vin ?? undefined,
            [formKeys.installedDate]: additionalInformation?.installedDate ? DateUtils.serverToUTC(additionalInformation?.installedDate) : undefined,
            [formKeys.failedDate]: additionalInformation?.failedDate ? DateUtils.serverToUTC(additionalInformation?.failedDate) : undefined,
            [formKeys.installedMileage]: additionalInformation?.installedMileage ?? undefined,
            [formKeys.failedMileage]: additionalInformation?.failedMileage ?? undefined,
            [formKeys.remarks]: additionalInformation?.remarks ?? undefined,
        })
    }, [additionalInformation, makes, models])

    /**
     * Handles changes in the make drop box and resets the value of the model with any change
     * @param e
     * @param form
     */
    const onMakeChanged = (e, form) => {
        form.change(e.target.name, e.target.value);
        if (!e.target.value)
            setIsMakeSelected(false);
        else
            setIsMakeSelected(true);
        form.change(formKeys.model, null);
    }

    /**
     * Submits the additional information to the server
     * @param values        the values to be submitted
     * @returns {Promise<void>}
     */
    const submitAdditionalInformation = async (values) => {
        setLoading(true);
        const forApi = {
            requestId: requestId,
            makeId: !!values.make ? values.make.id : null,
            modelId: !!values.model ? values.model.id : null,
            year: !!values.year ? Number(values.year) : null,
            vin: !!values.vin ? values.vin : null,
            installedDate: !!values.installedDate ? DateUtils.UTCToServer(values.installedDate).format(DateUtils.withoutOffsetFormat) : null,
            failedDate: !!values.failedDate ? DateUtils.UTCToServer(values.failedDate).format(DateUtils.withoutOffsetFormat) : null,
            installedMileage: !!values.installedMileage ? parseFloat(values.installedMileage) : null,
            failedMileage: !!values.failedMileage ? parseFloat(values.failedMileage) : null,
            remarks: values.remarks,
            updatedProperties: [
                "makeId",
                "modelId",
                "year",
                "vin",
                "installedDate",
                "failedDate",
                "installedMileage",
                "failedMileage",
                "Remarks"
            ]
        };
        const response = await Api.updatePartSupportAdditionalInformation(forApi);
        if (!isMounted())
            return;
        setLoading(false);
        if (response.resultFlag) {
            delete forApi.updatedProperties;
            delete forApi.requestId;
            onSuccess(forApi);
            setOpen(false);
        }
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={setOpen}
                maxWidth={'sm'}
                loading={loading}
                className={'bound-dialog part-support-additional-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        Additional Information
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2'}>
                    <Col xs={12} className={'mb-4'}>
                        <Form
                            className='d-flex flex-wrap w-100 align-items-start'
                            onSubmit={submitAdditionalInformation}
                            id={'form-id'}
                            validate={validate}
                            initialValues={initialValues}
                            render={({values, form}) => {
                                return (
                                    <>
                                        <div className={'truck-information'}>
                                            <Row className={'d-flex align-items-center justify-content-center w-100'}>
                                                <Col xs={12} className={'mb-3 p-0'}>
                                                    <p className={'title'}>
                                                        Truck Information
                                                    </p>
                                                </Col>
                                                <Col xs={12} className={'p-0 mb-3'}>
                                                    <OutlinedDropdownForm
                                                        name={formKeys.make}
                                                        label={'Make'}
                                                        required={false}
                                                        onChange={(e) => onMakeChanged(e, form)}
                                                        data={[
                                                            {id: -1, label: 'None', value: null},
                                                            ...(makes.map((e) => ({
                                                                    id: e.id,
                                                                    label: e.name,
                                                                    value: e
                                                                })
                                                            ) ?? [])]}
                                                    />
                                                </Col>
                                                {
                                                    isMakeSelected &&
                                                    <Col xs={12} className={'p-0 mb-3'}>
                                                        <OutlinedDropdownForm
                                                            name={formKeys.model}
                                                            label={'Model'}
                                                            required={false}
                                                            data={
                                                                [
                                                                    {
                                                                        id: -1,
                                                                        label: "None",
                                                                        value: null
                                                                    },
                                                                    ...(models.filter(t => t.makeId === values[formKeys.make]?.id).map((e) => ({
                                                                            id: e.id,
                                                                            label: e.name,
                                                                            value: e
                                                                        })
                                                                    ) ?? [])
                                                                ]
                                                            }
                                                        />
                                                    </Col>
                                                }
                                                <Row className={'d-flex justify-content-center align-items-start w-100'}>
                                                    <Col xs={6} className={'p-0 mb-3 pr-1'}>
                                                        <OutlinedInputForm
                                                            value={values[formKeys.year] ?? ''}
                                                            label={'Year'}
                                                            name={formKeys.year}
                                                            type={'text'}

                                                            required={required[formKeys.year]}
                                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                                            maxLength={4}
                                                            inputProps={{
                                                                maxLength: 4,
                                                                minLength: 4,
                                                            }}
                                                        />
                                                    </Col>
                                                    <Col xs={6} className={'p-0 mb-3 pl-1'}>
                                                        <OutlinedInputForm
                                                            value={values[formKeys.vin] ?? ''}
                                                            label={'VIN'}
                                                            name={formKeys.vin}
                                                            type={'text'}

                                                            required={required[formKeys.vin]}
                                                            onChange={(e) => form.change(e.target.name, e.target.value)}
                                                            maxLength={17}
                                                            inputProps={{
                                                                maxLength: 17,
                                                                minLength: 1,
                                                            }}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Row>
                                        </div>
                                        <Col xs={6} className={'p-0 mb-3 pr-1'}>
                                            <MuiRFF.DatePicker
                                                name={formKeys.installedDate}
                                                label={"Installed Date"}
                                                required={required[formKeys.installedDate]}
                                                maxDate={values[formKeys.failedDate]}
                                                disableFuture
                                            />
                                        </Col>
                                        <Col xs={6} className={'p-0 mb-3 pl-1'}>
                                            <MuiRFF.DatePicker
                                                name={formKeys.failedDate}
                                                label={"Failed Date"}
                                                required={required[formKeys.failedDate]}
                                                minDate={values[formKeys.installedDate]}
                                                disableFuture
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-3'}>
                                            <OutlinedInputForm
                                                fullWidth
                                                name={formKeys.installedMileage}
                                                placeholder={''}
                                                label={"Installed Mileage"}
                                                className={'mr-2'}
                                                type={'number'}
                                                required={required[formKeys.installedMileage]}
                                                value={values[formKeys.installedMileage] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value === '' ? undefined : e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-4'}>
                                            <OutlinedInputForm
                                                fullWidth
                                                name={formKeys.failedMileage}
                                                placeholder={''}
                                                label={"Failed Mileage"}
                                                className={'mr-2'}
                                                type={'number'}
                                                required={required[formKeys.failedMileage]}
                                                value={values[formKeys.failedMileage] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value === '' ? undefined : e.target.value)}
                                            />
                                        </Col>
                                        <Col xs={12} className={'p-0 mb-4'}>
                                            <OutlinedInputForm
                                                fullWidth
                                                name={formKeys.remarks}
                                                placeholder={''}
                                                label={"Remarks"}
                                                className={'mr-2'}
                                                required={required[formKeys.remarks]}
                                                value={values[formKeys.remarks] ?? ''}
                                                onChange={(e) => form.change(e.target.name, e.target.value)}
                                            />
                                        </Col>
                                    </>
                                )
                            }}
                        />
                    </Col>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => setOpen(false)}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                color={'primary'}
                                type={'submit'}
                                form={'form-id'}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {
                                    loading
                                        ? "Loading..."
                                        : "Save"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default PartSupportAdditionalInformationDialog;
